import { extractListItemEl } from '@canyon/utils'

import { ResumeSortableSections, Resumes } from '@gql/graphql'

import { TResumeBuilderForm } from '@/forms/types'
import { TPublicResume } from '@/store/resumeBuilderStore'

type TResumeDataType = Resumes | TPublicResume | undefined
type TResumeFormType = TResumeBuilderForm

interface IBaseResumeData {
  email: string
  firstName: string
  lastName: string
  linkedinUrl?: null | string
  location?: null | string
  name?: null | string
  phoneNumber?: null | string
  professionalSummary?: null | string
  sectionsOrder?: ResumeSortableSections[] | null
  targetTitle?: null | string
  website?: null | string
}

// Shared visibility fields between form and API data
const visibilityFields = {
  hideCertifications: false,
  hideCourseworks: false,
  hideEducations: false,
  hideInvolvements: false,
  hideProjects: false,
  hideSkills: false,
  hideSummary: false,
  hideTargetTitle: false,
  hideWorkExperiences: false,
} as const

function getBaseFields(data: TResumeDataType | TResumeFormType): IBaseResumeData {
  return {
    email: data?.email || '',
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    linkedinUrl: data?.linkedinUrl ?? '',
    location: data?.location ?? '',
    name: data?.name ?? '',
    phoneNumber: data?.phoneNumber ?? '',
    professionalSummary: data?.professionalSummary ?? '',
    sectionsOrder: data?.sectionsOrder ?? [],
    targetTitle: data?.targetTitle ?? '',
    website: data?.website ?? '',
  }
}

function getVisibilityFields(data: TResumeDataType | TResumeFormType) {
  return Object.keys(visibilityFields).reduce(
    (acc, key) => ({
      ...acc,
      [key]: data?.[key],
    }),
    {}
  )
}

export function serializeResumeData(resume: TResumeDataType): Partial<TResumeFormType> {
  if (!resume) return {}

  return {
    ...getBaseFields(resume),
    ...getVisibilityFields(resume),

    certificationsAttributes:
      resume.certifications?.map((certification) => ({
        ...certification,
        provider: certification.provider ?? undefined,
      })) ?? [],

    courseworksAttributes:
      resume.courseworks?.map((coursework) => ({
        ...coursework,
        achievements: coursework.achievements,
        currentlyWorkingOnCourse: coursework.currentlyWorkingOnCourse,
        endDate: coursework.endDate,
        id: coursework.id,
        institutionName: coursework.institutionName ?? '',
        name: coursework.name ?? '',
        showYearOnlyEndDate: coursework.showYearOnlyEndDate,
      })) ?? [],

    customSectionsAttributes: resume.customSections?.map((customSection) => ({
      customSectionExperiencesAttributes: customSection.customSectionExperiences?.map(
        (customSectionExperience) => ({
          ...customSectionExperience,
          name: customSectionExperience.name ?? '',
        })
      ),
      id: customSection.id,
      name: customSection.name ?? '',
    })),

    educationsAttributes:
      resume.educations?.map((education) => ({
        ...education,
        additionalInformation: education.additionalInformation ?? undefined,
        degree: education.degree ?? undefined,
        endDate: education.endDate,
        gpa: education.gpa ?? undefined,
        location: education.location ?? undefined,
        showYearOnlyEndDate: education.showYearOnlyEndDate,
        showYearOnlyStartDate: education.showYearOnlyStartDate,
        startDate: education.startDate,
      })) ?? [],

    groupedSkillsAttributes: resume.groupedSkills?.map((groupedSkill) => ({
      ...groupedSkill,
      category: groupedSkill.category ?? undefined,
    })),

    involvementsAttributes:
      resume.involvements?.map((involvement) => ({
        ...involvement,
        achievements: involvement.achievements,
        currentlyWorkingOnInvolvement: involvement.currentlyWorkingOnInvolvement,
        endDate: involvement.endDate,
        id: involvement.id,
        name: involvement.name ?? '',
        organizationName: involvement.organizationName ?? '',
        showYearOnlyEndDate: involvement.showYearOnlyEndDate,
        showYearOnlyStartDate: involvement.showYearOnlyStartDate,
        startDate: involvement.startDate,
      })) ?? [],

    projectsAttributes:
      resume.projects?.map((project) => ({
        achievements: project.achievements,
        currentlyWorkingOnProject: project.currentlyWorkingOnProject,
        endDate: project.endDate,
        id: project.id,
        linkUrl: project.linkUrl ?? undefined,
        name: project.name,
        showYearOnlyEndDate: project.showYearOnlyEndDate,
        showYearOnlyStartDate: project.showYearOnlyStartDate,
        startDate: project.startDate,
      })) ?? [],

    workExperiencesAttributes: resume.workExperiences?.map(
      ({ workPositions, ...workExperience }) => ({
        companyDescription: workExperience.companyDescription ?? undefined,
        companyName: workExperience.companyName,
        id: workExperience.id,
        workPositionsAttributes: workPositions?.map((workPosition) => ({
          achievements: workPosition.achievements,
          currentlyInPosition: workPosition.currentlyInPosition,
          endDate: workPosition.endDate,
          id: workPosition.id,
          location: workPosition.location ?? undefined,
          name: workPosition.name,
          showYearOnlyEndDate: workPosition.showYearOnlyEndDate,
          showYearOnlyStartDate: workPosition.showYearOnlyStartDate,
          startDate: workPosition.startDate,
        })),
      })
    ),
  }
}

export function serializeResumeFormData(formValues: TResumeFormType) {
  return {
    ...getBaseFields(formValues),
    ...getVisibilityFields(formValues),

    certificationsAttributes: formValues.certificationsAttributes.map((certification) => ({
      ...certification,
      generatedId: undefined,
    })),

    courseworksAttributes: formValues.courseworksAttributes,

    customSectionsAttributes: (formValues.customSectionsAttributes ?? []).map(
      (customSection, customSectionIndex) => ({
        ...customSection,
        customSectionExperiencesAttributes: customSection.customSectionExperiencesAttributes.map(
          (experience, experienceIndex) => ({
            ...experience,
            endDate: !(formValues.customSectionsAttributes ?? [])[customSectionIndex]
              .customSectionExperiencesAttributes[experienceIndex].currentlyInExperience
              ? experience.endDate
              : null,
          })
        ),
      })
    ),

    educationsAttributes: formValues.educationsAttributes.map((education, index) => ({
      ...education,
      endDate: !formValues.educationsAttributes[index].currentlyInEducation
        ? education.endDate
        : null,
    })),

    groupedSkillsAttributes: formValues.groupedSkillsAttributes.map((groupedSkill) => ({
      ...groupedSkill,
      skills: groupedSkill.skills ?? [],
    })),

    involvementsAttributes: formValues.involvementsAttributes.map((involvement, index) => ({
      ...involvement,
      endDate: !formValues.involvementsAttributes[index].currentlyWorkingOnInvolvement
        ? involvement.endDate
        : null,
    })),

    projectsAttributes: formValues.projectsAttributes.map((project, index) => ({
      ...project,
      endDate: !formValues.projectsAttributes[index].currentlyWorkingOnProject
        ? project.endDate
        : null,
    })),

    workExperiencesAttributes: formValues.workExperiencesAttributes.map(
      (workExperience, workExperienceIndex) => ({
        ...workExperience,
        workPositionsAttributes: workExperience.workPositionsAttributes.map(
          (position, positionIndex) => ({
            ...position,
            endDate: !formValues.workExperiencesAttributes[workExperienceIndex]
              .workPositionsAttributes[positionIndex].currentlyInPosition
              ? position.endDate
              : null,
          })
        ),
      })
    ),
  }
}
